// Static data for the GenX Technologies website
import {
  Cctv,
  Fingerprint,
  Flame,
  Cpu,
  ShieldAlert,
  MonitorCog,
} from "lucide-react";

export const COMPANY = {
  name: "GenX Technologies",
  tagline: "Think Smart, Stay Secure",
  subtagline: "We secure today for your tomorrow",
  founded: 2011,
  founder: "Akash Thakker",
  experienceYears: 15,
  email: "info@gen-x-technologies.com",
  supportEmail: "support@gen-x-technologies.com",
  phones: ["+91 9979084038", "+91 9998249263"],
  salesPhones: ["+91 9376122675", "+91 9925844038"],
  supportPhone: "+91 9979084016",
  landline: "079-46018659",
  address:
    "904, Safal Prelude, Beside Riviera Antilia, Corporate Road, Prahlad Nagar, Ahmedabad - 380015",
  hours: "Mon–Sat, 10:00 AM – 7:00 PM",
  gem: "Available on GeM (Government e Marketplace)",
};

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/solutions", label: "Solutions" },
  { to: "/about", label: "About" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

export const SOLUTIONS = [
  {
    id: "surveillance",
    slug: "surveillance-systems",
    title: "Surveillance Systems",
    subtitle: "CCTV / IP Cameras",
    icon: Cctv,
    short:
      "Analog, HD, AHD, HDCVI and IP camera deployments with remote monitoring and AI-assisted analytics.",
    description:
      "Our remote surveillance systems combine industrial-grade cameras, redundant storage and analytics-ready video pipelines so you have eyes everywhere — 24x7. We deploy across the full spectrum (Analog, HD, AHD, HDCVI and IP), and architect the storage, networking and access policies that go with it.",
    features: [
      "Indoor / outdoor PTZ, dome and bullet camera arrays",
      "On-prem NVR + optional cloud backup with retention policies",
      "AI motion, loitering, line-crossing & people-count analytics",
      "Mobile + browser live view with role-based access",
      "Integration with access control & alarms for verified incidents",
    ],
    partners: ["Dahua", "CP Plus", "Honeywell", "Sparsh", "Hikvision", "Uniview", "Matrix", "Prama", "Hi-Focus"],
    image: "https://images.pexels.com/photos/430208/pexels-photo-430208.jpeg",
  },
  {
    id: "access",
    slug: "access-control",
    title: "Access Control",
    subtitle: "Biometrics / Smart Locks",
    icon: Fingerprint,
    short:
      "Single-door to enterprise multi-door access with face, fingerprint, RFID and password authentication.",
    description:
      "Replace the friction of traditional lock-and-key with modern, audit-ready access control. From single-door biometric readers to enterprise multi-door systems with anti-passback and time-zone policies, we secure every threshold and keep a full digital trail.",
    features: [
      "Face / fingerprint / card / password / mobile credentials",
      "Single-door, multi-door and turnstile-grade controllers",
      "Time & attendance integration with payroll exports",
      "Visitor management with photo capture & pre-approval",
      "Anti-passback, time zones, lock-down & emergency unlock",
    ],
    partners: ["eSSL", "Realtime", "Secureye", "Timewatch", "BioMax", "ZKTeco", "Matrix"],
    image:
      "https://static.prod-images.emergentagent.com/jobs/ecb87ced-3422-4dca-a44d-a50b775e7ae1/images/fbe874156ca3a6c5735a1fa599181c8edea552eaca027dd846adc4fa7f3a223a.png",
  },
  {
    id: "fire",
    slug: "fire-safety",
    title: "Fire Safety",
    subtitle: "Detection & Suppression",
    icon: Flame,
    short:
      "Conventional and addressable fire alarm panels with hydrant systems, evacuation alerts and code compliance.",
    description:
      "Life-safety is non-negotiable. We design and install conventional and addressable fire detection systems, fire hydrant networks and integrated evacuation messaging — all tested, commissioned and maintained under AMC to keep you 100% code-compliant.",
    features: [
      "Conventional & addressable fire alarm panels",
      "Smoke, heat, multi-criteria & beam detectors",
      "Fire hydrant systems with pump house automation",
      "Public-address evacuation messaging integration",
      "Periodic testing, drills and AMC reporting",
    ],
    partners: ["Honeywell", "Bosch", "Ravel", "Kanex", "System Sensor", "Agni", "Apollo", "Safex"],
    image: "https://images.pexels.com/photos/19107333/pexels-photo-19107333.jpeg?_gl=1*kyuenw*_ga*ODc1MzI0NDQ5LjE3ODQyOTY2NzQ.*_ga_8JE65Q40S6*czE3ODQyOTY2NzQkbzEkZzEkdDE3ODQyOTgyNjkkajI5JGwwJGgw",
  },
  {
    id: "automation",
    slug: "office-home-automation",
    title: "Office & Home Automation",
    subtitle: "Modular & Scalable",
    icon: Cpu,
    short:
      "Centralised dashboards for lighting, climate, AV and security — modular, scalable and remotely manageable.",
    description:
      "One app for lights, ACs, blinds, music, security and energy. Our automation packages are modular — start with lighting and security, scale up to full climate, irrigation, AV and entertainment control without re-wiring. Designed for both luxury homes and modern office floors.",
    features: [
      "Lighting, climate, blinds, audio-video and security in one app",
      "Voice (Alexa / Google) and schedule-based scenes",
      "Energy dashboards with utility-bill forecasting",
      "Conference room automation (presence, video, AV)",
      "Modular build — start small, scale anytime",
    ],
    partners: ["Legrand", "Crestron", "Bosch", "Panasonic", "Matrix"],
    image: "https://images.pexels.com/photos/4239013/pexels-photo-4239013.jpeg",
  },
  {
    id: "intruder",
    slug: "intruder-detection",
    title: "Intruder Detection",
    subtitle: "Wired & Wireless Alarms",
    icon: ShieldAlert,
    short:
      "Perimeter and indoor intrusion systems with instant alerting, geofencing and 24/7 monitoring response.",
    description:
      "Layered intrusion protection that thinks. We combine wired and wireless burglar alarm panels with motion sensors, glass-break detectors, vibration sensors and CCTV verification so that every alarm is real, actionable and instantly routed to the right responder.",
    features: [
      "Wired & wireless burglar alarm panels",
      "PIR, dual-tech, glass-break and vibration sensors",
      "Mobile push, SMS and voice-call escalation",
      "Geofenced auto-arm / disarm",
      "CCTV-verified alarms to eliminate false positives",
    ],
    partners: ["Ajax", "Securico", "Honeywell", "Bosch"],
    image: "https://images.pexels.com/photos/279810/pexels-photo-279810.jpeg",
  },
  {
    id: "command",
    slug: "command-control-room",
    title: "Command Control Room",
    subtitle: "Unified Operations",
    icon: MonitorCog,
    short:
      "Purpose-built CCRs with video walls, redundant compute and operator workstations for mission-critical sites.",
    description:
      "When seconds matter, your operators need one screen, not ten. We build purpose-engineered Command Control Rooms with video walls, redundant servers, operator workstations and incident-workflow software — aggregating CCTV, access, alarms, fire and PA into a single, mission-grade interface.",
    features: [
      "Modular video wall design (LED / LCD / projection)",
      "Multi-vendor aggregation (CCTV + access + fire + PA)",
      "Redundant compute, UPS-backed power, disaster-ready",
      "Incident-workflow software with SOP automation",
      "Custom operator ergonomics & shift handover tools",
    ],
    partners: ["Bosch", "Dahua", "Crestron", "Cisco", "Polycom", "Yealink"],
    image: "https://images.pexels.com/photos/32026177/pexels-photo-32026177.jpeg",
  },
  {
    id: "attendancerecord",
    slug: "attendance-record",
    title: "Attendance Record",
    subtitle: "Smart Tracking",
    icon: MonitorCog,
    short: "Smart workforce management starts here.",
    description:
      "Streamline your payroll, eliminate time-theft, and gain real-time visibility into your workforce. Our advanced Attendance Record systems utilize cutting-edge biometric (fingerprint and facial recognition) and RFID technology to deliver foolproof time tracking for businesses of any size.",
    features: [
      "Biometric & facial recognition for fast, accurate attendance",
      "Cloud-based connectivity for real-time attendance logs",
      "Seamless integration with HRMS and payroll software",
      "Power backup for uninterrupted operation",
      "Comprehensive daily, weekly and monthly reports",
    ],
    partners: ["Bosch", "Dahua", "Crestron", "Cisco", "Polycom", "Yealink"],
    image: "https://images.pexels.com/photos/37538043/pexels-photo-37538043.jpeg",
  },
  {
    id: "automaticboombarrier",
    slug: "automatic-boom-barrier",
    title: "Automatic Boom Barrier",
    subtitle: "Intelligent Access Control",
    icon: MonitorCog,
    short: "High-speed vehicle access control.",
    description:
      "Secure your entry and exit points with our heavy-duty Automatic Boom Barriers. Designed for continuous, high-frequency operation, these barriers provide efficient traffic management and enhanced security for residential complexes, commercial hubs, and industrial sites.",
    features: [
      "Modular video wall design (LED / LCD / projection)",
      "Multi-vendor aggregation (CCTV + access + fire + PA)",
      "Redundant compute, UPS-backed power, disaster-ready",
      "Incident-workflow software with SOP automation",
      "Custom operator ergonomics & shift handover tools",
    ],
    partners: ["Bosch", "Dahua", "Crestron", "Cisco", "Polycom", "Yealink"],
    image: "https://images.pexels.com/photos/12608612/pexels-photo-12608612.jpeg?_gl=1*jor5tx*_ga*ODc1MzI0NDQ5LjE3ODQyOTY2NzQ.*_ga_8JE65Q40S6*czE3ODQyOTY2NzQkbzEkZzEkdDE3ODQyOTk3MzEkajUyJGwwJGgw",
  },
  {
    id: "burglaralarm",
    slug: "burglar-alarm",
    title: "Burglar Alarm",
    subtitle: "Intelligent Security",
    icon: MonitorCog,
    short: "Your 24/7 silent guardian.",
    description:
      "Protect your assets and loved ones with our intelligent Burglar Alarm Systems. Offering multi-zone protection, this system instantly detects unauthorized entry, glass breakages, or motion, triggering high-decibel alerts and immediate notifications to your smartphone.",
    features: [
      "Modular video wall design (LED / LCD / projection)",
      "Multi-vendor aggregation (CCTV + access + fire + PA)",
      "Redundant compute, UPS-backed power, disaster-ready",
      "Incident-workflow software with SOP automation",
      "Custom operator ergonomics & shift handover tools",
    ],
    partners: ["Bosch", "Dahua", "Crestron", "Cisco", "Polycom", "Yealink"],
    image: "https://images.pexels.com/photos/27662879/pexels-photo-27662879.jpeg?_gl=1*wbbr20*_ga*ODc1MzI0NDQ5LjE3ODQyOTY2NzQ.*_ga_8JE65Q40S6*czE3ODQyOTY2NzQkbzEkZzEkdDE3ODQyOTk2MzUkajYwJGwwJGgw",
  },
  {
    id: "epabx",
    slug: "epabx",
    title: "EPABX System",
    subtitle: "Intelligent Communication",
    icon: MonitorCog,
    short: "Seamless communication, unified productivity.",
    description:
      "Transform your internal and external business communication with our scalable EPABX Systems. Built for modern workplaces, it ensures crystal-clear voice routing, reduces call costs, and bridges the gap between teams through feature-rich telephony.",
    features: [
      "Smart call routing with auto-attendant and IVR",
      "Scalable architecture with easy extension expansion",
      "Cost-efficient internal calling across departments",
      "Call recording and monitoring for quality assurance",
      "Caller ID and call forwarding to never miss important calls",
    ],
    partners: ["Bosch", "Dahua", "Crestron", "Cisco", "Polycom", "Yealink"],
    image: "https://images.pexels.com/photos/17636234/pexels-photo-17636234.jpeg",
  },
  {
    id: "audioandvideoconference",
    slug: "audio-and-video-conference",
    title: "Audio and Video Conference Systems",
    subtitle: "Communication",
    icon: MonitorCog,
    short: "Bring your global teams into the same room.",
    description:
      "Experience lag-free, face-to-face collaboration with our premium Audio and Video Conference Systems. Designed for huddle rooms, executive boardrooms, and remote setups, our systems deliver ultra-high-definition video and cinema-grade audio clarity.",
    features: [
      "4K ultra-HD video with auto-framing and speaker tracking",
      "360° omnidirectional audio with echo cancellation",
      "Universal compatibility with Zoom, Teams, Google Meet and Webex",
      "Dual-screen support for presentations and participant view",
      "Wireless content sharing from laptops and mobile devices",
    ],
    partners: ["Bosch", "Dahua", "Crestron", "Cisco", "Polycom", "Yealink"],
    image: "https://images.pexels.com/photos/6949366/pexels-photo-6949366.jpeg",
  },
];

export const PARTNER_BRANDS = [
  {
    name: "Bosch",
    logo: "/logos/bosch.png",
  },
  {
    name: "Dahua",
    logo: "/logos/dahua.png",
  },
  {
    name: "Cisco",
    logo: "/logos/cisco.png",
  },
  {
    name: "Honeywell",
    // Official SVG
    logo: "/logos/honeywell.png",
  },
  {
    name: "Panasonic",
    logo: "/logos/panasonic.png",
  },
  {
    name: "Samsung",
    logo: "/logos/samsung.png",
  },
  {
    name: "Yamaha",
    logo: "/logos/yamaha.png",
  },
  {
    name: "JBL",
    logo: "/logos/jbl.png",
  },
  {
    name: "LG",
    logo: "/logos/lg.png",
  },
  {
    name: "Legrand",
    logo: "/logos/legrand.png",
  },
  {
    name: "D-Link",
    logo: "/logos/d-link.png",
  },
  {
    name: "CP Plus",
    logo: "/logos/cp-plus.png",
  },
  {
    name: "sparsh",
    logo: "/logos/sparsh.png",
  },
  {
    name: "hikvision",
    logo: "/logos/hikvision.png",
  },
  {
    name: "uniview",
    logo: "/logos/uniview.png",
  },
  {
    name: "matrix",
    logo: "/logos/matrix.png",
  },
  {
    name: "prama",
    logo: "/logos/prama.png",
  },
  {
    name: "hi-focus",
    logo: "/logos/hi-focus.png",
  },
  {
    name: "crestron",
    logo: "/logos/crestron.png",
  },
  {
    name: "polycom",
    logo: "/logos/polycom.png",
  },
  {
    name: "yealink",
    logo: "/logos/yealink.png",
  },
  {
    name: "essl",
    logo: "/logos/essl.png",
  },
  {
    name: "realtime",
    logo: "/logos/realtime.png",
  },
  {
    name: "secureye",
    logo: "/logos/secureye.png",
  },
  {
    name: "timewatch",
    logo: "/logos/timewatch.png",
  },
  {
    name: "biomax",
    logo: "/logos/biomax.png",
  },
   {
    name: "zkteco",
    logo: "/logos/zkteco.png",
  },
  {
    name: "ravel",
    logo: "/logos/ravel.png",
  },
  {
    name: "kanex",
    logo: "/logos/kanex.png",
  },
  {
    name: "system sensor",
    logo: "/logos/system-sensor.png",
  },
  {
    name: "safex",
    logo: "/logos/safex.png",
  },
  {
    name: "securico",
    logo: "/logos/securico.png",
  },
  {
    name: "ajax",
    logo: "/logos/ajax.png",
  },
  {
    name: "apollo",
    logo: "/logos/apollo.png",
  },
  {
    name: "agni",
    logo: "/logos/agni.avif",
  },
];

export const PARTNER_TYPED = [
  "Dahua", "Hikvision", "CP PLUS", "Matrix", "Uniview", "ZKTeco", "Ajax",
  "Polycom", "Yealink", "Crestron", "Shure", "Denon", "eSSL", "Realtime",
  "BioMax", "Apollo", "Ravel", "Sparsh", "NEC", "Grandstream",
];

export const ESTEEMED_CLIENTS = [
  { name: "Cadila Pharmaceuticals", logo: "/client-logos/cadila.png" },
  { name: "Eris", logo: "/client-logos/eris.png"},
  { name: "Indian Council of Medical Research", logo: "/client-logos/india-council-of-medical-research.png" },
  { name: "Gujarat RERA", logo: "/client-logos/gujarat-rera.png"},
  { name: "HUDCO (A Navratna CPSE)", logo: "/client-logos/hudco.png" },
  { name: "Ahmedabad Municipal Corporation", logo: "/client-logos/amc.png" },
  { name: "Vadodara Municipal Corporation", logo: "/client-logos/vmc.png" },
  { name: "Gandhinagar Mahanagarpalika", logo: "/client-logos/gmc.png" },
  { name: "Civil Hospital Amdavad", logo: "/client-logos/civil-hospital-ahmedabad.png" },
  { name: "YIZUMI", logo: "/client-logos/yizumi.png" },
  { name: "Kadam", logo: "/client-logos/kadam.png" },
  { name: "Brillia", logo: "/client-logos/brillia.png" }
];

export const PILLARS = [
  {
    label: "Safety",
    title: "Engineered for life-critical environments",
    description:
      "Fire safety, evacuation systems and intruder detection certified to meet enterprise compliance standards.",
  },
  {
    label: "Security",
    title: "Layered protection across every perimeter",
    description:
      "From biometric access to AI-driven surveillance, we design defense-in-depth that scales with your operations.",
  },
  {
    label: "Intelligence",
    title: "One pane of glass for every system",
    description:
      "Command control rooms unify video, access, alarms and automation into a single, operator-grade interface.",
  },
];

export const TRUST_SIGNALS = [
  { label: "24/7 Monitoring", value: "365 Days" },
  { label: "Encrypted Pipelines", value: "AES-256" },
  { label: "Certified Hardware", value: "OEM-Grade" },
  { label: "Years in Business", value: "13+" },
];

export const FAQS = [
  {
    q: "Do you provide customized security audits?",
    a: "Yes. Our certified consultants perform on-site risk assessments, threat modeling and gap analyses to design a security architecture tailored to your facility, workflows and compliance posture.",
  },
  {
    q: "Are the automation systems compatible with existing hardware?",
    a: "Absolutely. Our integration layer supports leading OEMs (Bosch, Honeywell, Dahua, Hikvision, Cisco, Panasonic, ZKTeco and more) so you can extend rather than replace what you already operate.",
  },
  {
    q: "What is the response time for technical support?",
    a: "Standard SLAs guarantee remote acknowledgement within 30 minutes and on-site response within 4 hours for critical incidents. Premium AMC tiers include 24/7 priority dispatch.",
  },
  {
    q: "Can I manage all these systems through a single interface?",
    a: "Yes. Our Command Control Room solutions and Automation packages aggregate CCTV, access, alarms, fire and AV into one centralized, operator-friendly dashboard.",
  },
  {
    q: "Does GenX Technologies provide post-installation maintenance?",
    a: "We offer comprehensive Annual Maintenance Contracts (AMC) covering preventive servicing, firmware updates and emergency response to keep your systems 100% operational.",
  },
  {
    q: "Are your home automation systems scalable?",
    a: "Our solutions are fully modular. Start with lighting and security, then scale to climate, media, irrigation and energy management — without re-wiring.",
  },
  {
    q: "Do you serve government and PSU projects?",
    a: "Yes. We are listed on GeM (Government e Marketplace) and have delivered for organisations like ICMR, Gujarat RERA, HUDCO, Ahmedabad & Vadodara Municipal Corporations and Civil Hospital Amdavad.",
  },
  {
    q: "Which cities and regions do you cover?",
    a: "Our HQ is in Ahmedabad, Gujarat. We deliver pan-India for enterprise and government clients, with a primary service footprint across Gujarat, Maharashtra, Rajasthan and Madhya Pradesh.",
  },
];

export const SERVICE_INTERESTS = [
  "Surveillance Systems (CCTV)",
  "Access Control / Biometrics",
  "Fire Safety & Alarms",
  "Office / Home Automation",
  "Intruder Detection",
  "Command Control Room",
  "Annual Maintenance Contract",
  "Other",
];

export const HERO_IMAGE =
  "https://images.pexels.com/photos/32529341/pexels-photo-32529341.jpeg";

export const ABOUT_IMAGE =
  "https://images.pexels.com/photos/2882566/pexels-photo-2882566.jpeg";

export const CONTACT_IMAGE =
  "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg";
